import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import RichTextEditor from "../../../components/RichTextEditor";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  CreatingEvent,
  DeleteEvent,
  UpdateEvent,
} from "../../../services/EventsService";
import { useDispatch } from "react-redux";
import { loadEventsAction } from "../../../store/slices/calendarSlice";
import { toast } from "react-toastify";
import ConfirmModal from "../../../components/ConfirmModel";
import {
  DontAddClicked,
  EventButtons,
  IsEditing,
  IsErr,
} from "../../../components/calendar";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight.js";

function AddEvent({ addClicked, event, onEventAdded, setSelectedEvent }) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    setError,
    clearErrors,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const [err, setErr] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Watching form values to validate mandatory fields
  const name = watch("name");
  const date = watch("date");
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const overrideDefaultPricing = watch("overrideDefaultPricing");

  useEffect(() => {
    setIsFormValid(name && date && startTime && endTime);
  }, [name, date, startTime, endTime]);

  const setFormValues = useCallback(async () => {
    if (event) {
      setIsEditing(true);
      setValue("name", event.name);
      setValue("address1", event.address1);
      setValue("address2", event.address2);
      setValue("city", event.city);
      setValue("state", event.state);
      setValue("zip", event.zip);
      setValue("date", event.date);
      setValue("startTime", event.startTimeUTC);
      setValue("endTime", event.endTimeUTC);
      setValue("elevation", event.elevation || 0);
      setValue("wind", event.wind || 0);
      setValue("heightComment", event.heightComment);
      setValue("hasPositionDrills", event.hasPositionDrills ?? false);
      setValue("overrideDefaultPricing", event.overrideDefaultPricing ?? false);
      setValue("combinePrice", event.combinePrice ?? 0);
      setValue("combinePosDrillPrice", event.combinePosDrillPrice ?? 0);
      setValue("earlyCombinePrice", event.earlyCombinePrice ?? 0);
      setValue("dayOfCombinePrice", event.dayOfCombinePrice ?? 0);
      setValue("emailText", event.emailText ?? "");
      setSelectedEvent(event.id);
    } else {
      setIsEditing(false);
      reset({
        name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        date: "",
        startTime: "",
        endTime: "",
        elevation: 0,
        wind: 0,
        heightComment: "",
        hasPositionDrills: false,
        overrideDefaultPricing: false,
        combinePosDrillPrice: 0,
        earlyCombinePrice: 0,
        combinePrice: 0,
        dayOfCombinePrice: 0,
        emailText: "",
      });
      setSelectedEvent(null);
    }
  }, [reset, setValue, setSelectedEvent, event]);

  useEffect(() => {
    setFormValues(event);
  }, [event, setFormValues]);

  const onSubmit = async (data) => {
    setLoading(true);
    setErr(null);

    try {
      if (data.overrideDefaultPricing) {
        const pricingFields = [
          {
            key: "combinePosDrillPrice",
            label: "Position Drill Price",
          },
          {
            key: "earlyCombinePrice",
            label: "Early Combine Price",
          },
          {
            key: "combinePrice",
            label: "Two Week Combine Price",
          },
          {
            key: "dayOfCombinePrice",
            label: "Day of Combine Price",
          },
        ];

        let hasValidationError = false;

        pricingFields.forEach(({ key, label }) => {
          const value = data[key];

          if (value === "" || value === null || value === undefined) {
            setError(key, {
              type: "required",
              message: `${label} is required`,
            });
            hasValidationError = true;
          } else {
            clearErrors(key);
          }
        });

        if (hasValidationError) {
          setLoading(false);
          return;
        }

        const zeroFields = pricingFields.filter(
          ({ key }) => Number(data[key]) === 0,
        );

        if (zeroFields.length > 0) {
          const zeroFieldLabels = zeroFields
            .map((field) => field.label)
            .join(", ");

          const confirmed = window.confirm(
            `The following override pricing field(s) are set to 0: ${zeroFieldLabels}. Do you want to continue saving with zero pricing?`,
          );

          if (!confirmed) {
            setLoading(false);
            return;
          }
        }
      }

      if (data.emailText === "<p></p>") {
        data.emailText = "";
      }

      const response = isEditing
        ? await UpdateEvent(apiInstance, event?.id, data)
        : await CreatingEvent(apiInstance, data);

      if (response.status === 200) {
        if (isEditing) {
          toast.success("Event updated successfully!", {
            position: "top-right",
          });
        } else {
          toast.success("Event created successfully!", {
            position: "top-right",
          });
        }

        reset();
        onEventAdded();
        dispatch(loadEventsAction(false));
      } else {
        setErr(response.response.data.error);
      }
    } catch (err) {
      toast.error(err.message || "An error occurred", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => setShowDeleteModal(true);

  const handleDeleteConfirm = async () => {
    if (event) {
      setLoading(true);
      try {
        await DeleteEvent(apiInstance, event.id);
        toast.success("Event deleted successfully!", {
          position: "top-right",
        });
        reset();
        onEventAdded();
        dispatch(loadEventsAction(false));
      } catch (err) {
        toast.error(
          err.message || "An error occurred while deleting the event",
          { position: "top-right" },
        );
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
      }
    }
  };

  const handleDeleteCancel = () => setShowDeleteModal(false);

  const { adjustedHeight } = useResponsiveHeight({
    mobileHeight: 340,
    desktopHeight: 480,
  });

  const sectionClass = "px-2 py-0";
  const sectionTitleClass =
    "mb-3 text-xs font-semibold uppercase text-gray-400";
  const labelClass = "!text-gray-700 text-sm font-medium pb-1";
  const inputClass =
    "w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-500 focus:bg-white";
  const disabledInputClass = `${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500`;
  const checkboxClass = "!h-4 !w-4 cursor-pointer rounded border-gray-300";
  const priceRegister = (fieldName, label) =>
    register(fieldName, {
      validate: (value) => {
        if (!overrideDefaultPricing) return true;

        if (value === "" || value === null || value === undefined) {
          return `${label} is required`;
        }

        const parsedValue = Number(value);
        if (Number.isNaN(parsedValue)) {
          return `${label} must be a valid number`;
        }

        if (parsedValue < 0) {
          return `${label} cannot be negative`;
        }

        return true;
      },
      setValueAs: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "";
        }

        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? "" : parsedValue;
      },
    });

  return (
    <>
      {addClicked && (
        <div
          className="mx-auto max-w-5xl overflow-y-auto -mr-4 pr-4"
          style={{ height: `${adjustedHeight + 180}px` }}
        >
          <IsErr err={err} setErr={setErr} />
          <form
            className="form-field-add space-y-6 text-sm"
            onSubmit={handleSubmit(onSubmit)}
          >
            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Event Information</h2>
              <label className={labelClass}>Event Name</label>
              <input
                type="text"
                {...register("name")}
                placeholder="Event Name"
                className={inputClass}
                required
                maxLength={60}
              />
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Location</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Address 1</label>
                  <input
                    type="text"
                    {...register("address1")}
                    placeholder="Address 1"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Address 2</label>
                  <input
                    type="text"
                    {...register("address2")}
                    placeholder="Address 2"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    {...register("city")}
                    placeholder="City"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>State</label>
                  <input
                    type="text"
                    {...register("state")}
                    placeholder="State"
                    className={inputClass}
                  />
                </div>
                <div className="sm:max-w-[220px]">
                  <label className={labelClass}>Zip</label>
                  <input
                    type="text"
                    {...register("zip")}
                    placeholder="Zip"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Schedule</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2 sm:max-w-[260px]">
                  <label className={labelClass}>Event Date</label>
                  {/* Past events must remain editable; do not reintroduce past-date blocking. */}
                  {isEditing && (
                    <input
                      type="date"
                      {...register("date", { required: true })}
                      className={`${inputClass} bg-transparent appearance-none`}
                    />
                  )}
                  {!isEditing && (
                    <input
                      type="date"
                      {...register("date", { required: true })}
                      className={`${inputClass} bg-transparent appearance-none`}
                    />
                  )}
                </div>
                <div>
                  <label className={labelClass}>Start Time</label>
                  <input
                    type="time"
                    {...register("startTime")}
                    placeholder="Start Time"
                    className={`${inputClass} bg-transparent appearance-none`}
                  />
                </div>
                <div>
                  <label className={labelClass}>End Time</label>
                  <input
                    type="time"
                    {...register("endTime")}
                    placeholder="End Time"
                    className={`${inputClass} bg-transparent appearance-none`}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Environment</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Elevation</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={0.0}
                    {...register("elevation")}
                    placeholder="Elevation (feet)"
                    className={inputClass}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        e.target.value = 0;
                      }
                    }}
                  />
                </div>
                <div>
                  <label className={labelClass}>Wind</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={0.0}
                    {...register("wind")}
                    placeholder="Wind (mph)"
                    className={inputClass}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        e.target.value = 0;
                      }
                    }}
                  />
                </div>
                <div className="col-span-full">
                  <label className={labelClass}>
                    Comment for height measurements (optional)
                  </label>
                  <input
                    type="text"
                    {...register("heightComment")}
                    placeholder="Comment about height"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Athlete Options</h2>
              <div className="flex items-start gap-3 text-gray-700">
                <input
                  type="checkbox"
                  id="hasPositionDrills"
                  {...register("hasPositionDrills")}
                  className={checkboxClass}
                />
                <label
                  htmlFor="hasPositionDrills"
                  className="cursor-pointer text-sm !text-gray-700"
                >
                  Allow athletes to purchase position drills
                </label>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Email Text</h2>
              <p className="mb-3 text-xs text-gray-500">
                This text will be included in emails sent to attendees. Supports
                bold, italic, lists, and paragraph formatting.
              </p>
              <Controller
                name="emailText"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter email content to send to attendees..."
                  />
                )}
              />
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Custom Combine Pricing</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-700">
                  <input
                    type="checkbox"
                    id="overrideDefaultPricing"
                    {...register("overrideDefaultPricing")}
                    className={checkboxClass}
                  />
                  <label
                    htmlFor="overrideDefaultPricing"
                    className="text-sm !text-gray-700"
                  >
                    Override default pricing for this combine
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Position Drill Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "combinePosDrillPrice",
                        "Position Drill Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Early Combine Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "earlyCombinePrice",
                        "Early Combine Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Two Week Combine Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "combinePrice",
                        "Two Week Combine Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Day of Combine Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "dayOfCombinePrice",
                        "Day of Combine Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                  </div>
                </div>
              </div>
            </section>

            <EventButtons
              handleDelete={handleDelete}
              isEditing={isEditing}
              isFormValid={isFormValid}
              loading={loading}
            />
          </form>
          <IsEditing event={event} isEditing={isEditing} />
        </div>
      )}
      {!addClicked && <DontAddClicked />}
      <ConfirmModal
        show={showDeleteModal}
        message="Are you sure you want to delete this event?"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}

export default AddEvent;
